/**
 * Section constants for Anthropometric v2. Data comes from
 * api-anthropometric-v2-service; this file holds:
 *   • PARAMETER_META — the fixed 7-parameter registry (display name, kind,
 *     units, backend enum key) used to render both the table and the editor.
 *   • INTERPRETATION_BANDS + interpretationBand() — chip lookup for the "Reads as" column.
 *   • BLOOD_GROUPS — dropdown options for the blood-group parameter.
 */

/**
 * Flag bands. `key` matches backend enum:
 *   normal · underweight · overweight · obese-class-i · obese-class-ii ·
 *   obese-class-iii · elevated · high · unknown
 * Everything renders as a coloured chip; `unknown` renders as an em-dash.
 * The three obese sub-classes step through progressively darker reds so
 * severity reads at a glance without the label being cut off.
 */
export const INTERPRETATION_BANDS = {
  normal:            { key: "normal",            label: "Normal",     bg: "#e8f7ef", fg: "#1b7f4c", dot: "#1b7f4c" },
  underweight:       { key: "underweight",       label: "Underweight", bg: "#fdf1e2", fg: "#b45309", dot: "#b45309" },
  elevated:          { key: "elevated",          label: "Elevated",   bg: "#fdf1e2", fg: "#b45309", dot: "#b45309" },
  overweight:        { key: "overweight",        label: "Overweight", bg: "#fef1e6", fg: "#c2410c", dot: "#c2410c" },
  "obese-class-i":   { key: "obese-class-i",   label: "Obesity Class I",   bg: "#fdeaea", fg: "#b91c1c", dot: "#b91c1c" },
  "obese-class-ii":  { key: "obese-class-ii",  label: "Obesity Class II",  bg: "#fbd4d4", fg: "#991b1b", dot: "#991b1b" },
  "obese-class-iii": { key: "obese-class-iii", label: "Obesity Class III", bg: "#f8c1c1", fg: "#7f1d1d", dot: "#7f1d1d" },
  high:              { key: "high",              label: "High",       bg: "#fdeaea", fg: "#b91c1c", dot: "#b91c1c" },
  unknown:           { key: "unknown",           label: "—",          bg: "#f4f5f7", fg: "#6a7282", dot: "#8a94a6" },
};

export const interpretationBand = (key) => INTERPRETATION_BANDS[key] || INTERPRETATION_BANDS.unknown;

/**
 * Fixed parameter registry. Order here drives the table row order.
 *
 *   key         backend enum value (used in AddRequest.parameter)
 *   label       display name shown in the table + editor
 *   kind        "measure" | "select" | "auto" — drives the editor form
 *   units       array of unit toggle options; first is the metric-normalised default
 *   auto        true for BMI + WHR — not user-editable, no editor card
 *   viewable    true if the "View trend / audit" button appears in the Analysis column
 *   chart       true if the modal renders a line chart (Height opens the modal
 *               but skips the chart — audit-only per mock)
 */
export const PARAMETER_META = [
  { key: "height",             label: "Height",              kind: "measure", units: ["cm", "ft/in"], auto: false, viewable: true,  chart: false },
  { key: "weight",             label: "Weight",              kind: "measure", units: ["kg", "lb"],    auto: false, viewable: true,  chart: true  },
  { key: "bloodGroup",         label: "Blood group",         kind: "select",  units: [],              auto: false, viewable: true,  chart: false },
  { key: "bmi",                label: "BMI",                 kind: "auto",    units: ["kg/m²"],       auto: true,  viewable: true,  chart: true  },
  { key: "waistCircumference", label: "Waist circumference", kind: "measure", units: ["cm", "in"],    auto: false, viewable: true,  chart: true  },
  { key: "hipCircumference",   label: "Hip circumference",   kind: "measure", units: ["cm", "in"],    auto: false, viewable: true,  chart: true  },
  { key: "whr",                label: "WHR",                 kind: "auto",    units: [""],            auto: true,  viewable: true,  chart: true  },
];

// Convenient lookup by key.
export const PARAMETER_BY_KEY = PARAMETER_META.reduce((acc, p) => {
  acc[p.key] = p;
  return acc;
}, {});

// Blood-group select options.
//
// Two special entries beyond the standard eight ABO groups:
//   • "Other" — rare blood groups not covered by ABO (Bombay, Rh-null, etc.).
//     Stored as the literal "Other" in valueDisplay.
//   • "None"  — explicit "no value on record" pick used to unset a previously
//     recorded blood group. Handled specially by the editor: on save, sends
//     valueDisplay = "" so the content-table cell renders as "—".
export const BLOOD_GROUP_NONE = "None";
export const BLOOD_GROUPS = [
  { key: "A+", label: "A+" },
  { key: "A-", label: "A-" },
  { key: "B+", label: "B+" },
  { key: "B-", label: "B-" },
  { key: "AB+", label: "AB+" },
  { key: "AB-", label: "AB-" },
  { key: "O+", label: "O+" },
  { key: "O-", label: "O-" },
  { key: "Other", label: "Other" },
  { key: BLOOD_GROUP_NONE, label: "None" },
];

// Normalise a free-text blood group ("B positive", "b-pos", "o neg",
// "AB+") to one of the canonical BLOOD_GROUPS keys. Returns "" when
// the input doesn't resolve to a known value (empty or ambiguous) so
// the caller can fall back to the "None" sentinel. The transcript
// agent emits the group verbatim as the speaker said it (per the
// `value_display` field spec in add_anthropometric); without this
// collapse the dropdown would receive a non-matching value and show
// nothing selected — then Save would write the raw spoken string
// into the DB. Mirrored server-side in the Go anthropometric adapter
// so a stray wire-level mismatch still normalises on fan-out.
export const normaliseBloodGroup = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  // Fast path — already canonical (case-insensitive match on the key).
  const upper = s.toUpperCase();
  for (const o of BLOOD_GROUPS) {
    if (o.key === BLOOD_GROUP_NONE) continue;
    if (o.key.toUpperCase() === upper) return o.key;
  }
  // Canonicalise the ABO prefix + rhesus sign from a free-text form.
  // Prefix: AB wins over single A/B/O when both substrings appear.
  const lower = s.toLowerCase();
  let prefix = "";
  if (/\bab\b/.test(lower) || lower.startsWith("ab")) prefix = "AB";
  else if (/\ba\b/.test(lower) || lower.startsWith("a")) prefix = "A";
  else if (/\bb\b/.test(lower) || lower.startsWith("b")) prefix = "B";
  else if (/\bo\b/.test(lower) || lower.startsWith("o") || lower.includes("zero")) prefix = "O";
  if (!prefix) return "";
  let sign = "";
  if (lower.includes("+") || /\b(pos|positive|plus)\b/.test(lower)) sign = "+";
  else if (lower.includes("-") || /\b(neg|negative|minus)\b/.test(lower)) sign = "-";
  if (!sign) return "";
  return `${prefix}${sign}`;
};

// Metric ↔ imperial conversion constants + helpers, shared between the
// editor (input parsing) and the trend audit (dual-unit stacked display).
export const LB_PER_KG = 2.20462;
export const IN_PER_CM = 0.393701;
export const CM_PER_IN = 2.54;
export const KG_PER_LB = 0.453592;

// Format a metric numeric value into `{ primary, secondary }` display strings
// for the audit column (mock stacks both units for Weight / Waist / Hip):
//   Weight (kg)  → "72.5 kg" + "159.8 lb"
//   Height (cm)  → "180.3 cm" + "5'11\""
//   Waist / Hip  → "95 cm"   + "37.4 in"
//   BMI / WHR / Blood group → single-line, no secondary.
export const dualUnit = (parameter, numericValue, unit) => {
  if (numericValue == null || !isFinite(numericValue)) {
    return { primary: "—", secondary: "" };
  }
  const kg = numericValue;
  const cm = numericValue;
  switch (parameter) {
    case "weight": {
      const lb = kg * LB_PER_KG;
      return { primary: `${round1(kg)} kg`, secondary: `${round1(lb)} lb` };
    }
    case "height": {
      const totalIn = cm * IN_PER_CM;
      let feet = Math.floor(totalIn / 12);
      let inches = round1(totalIn - feet * 12);
      // 11.95 rounds to 12.0 — carry into the next foot so we never display
      // 5'12".
      if (inches >= 12) {
        feet += 1;
        inches = 0;
      }
      const inchesStr = Number.isInteger(inches) ? `${inches}` : inches.toFixed(1);
      return { primary: `${round1(cm)} cm`, secondary: `${feet}'${inchesStr}"` };
    }
    case "waistCircumference":
    case "hipCircumference": {
      const inches = cm * IN_PER_CM;
      return { primary: `${round1(cm)} cm`, secondary: `${round1(inches)} in` };
    }
    case "bmi":
      return { primary: `${round1(numericValue)} kg/m²`, secondary: "" };
    case "whr":
      return { primary: round2(numericValue).toFixed(2), secondary: "" };
    default:
      return { primary: `${numericValue}${unit ? ` ${unit}` : ""}`, secondary: "" };
  }
};

const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => Math.round(n * 100) / 100;
